import React, { useContext, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { fullName } from "../../../../../../utils/format";
import { currentlyChecked, originallyCheckedStudentsInPath } from "../utils";
import { HierarchyPath } from "../../../../../../components/TreeCheckboxGroup";
import { DismissableMessages } from "../../../../../../interfaces/Session";
import { commonMessages } from "../../../../../../utils/messages";
import { getStudent } from "../../../../../../utils/dataRetrieval";
import { sessionStore } from "../../../../../../contexts/SessionContext";
import MessageDismiss from "../../../../../../components/MessageDismiss/MessageDismiss";
import CondensedStudentsList from "../../../../../../components/CondensedStudentsList/CondensedStudentsList";

import "./ConfirmationModal.scss";
import { ModalProps } from "@evidenceb/athena-common/design-system/Modal";
import { Modal } from "@evidenceb/athena-common/design-system/Modal";

type Props = Pick<ModalProps, "open" | "onClose" | "onOk" | "onCancel"> & {
    checkedStudents: HierarchyPath[];
    originallyCheckedStudents: string[] | undefined;
    toggledPath: string | undefined;
};

const ConfirmationModal = ({
    open,
    onClose,
    onOk,
    onCancel,
    checkedStudents,
    originallyCheckedStudents,
    toggledPath,
}: Props) => {
    const intl = useIntl();
    const {
        session: { classrooms },
    } = useContext(sessionStore);

    const toggledStudents = useMemo(() => {
        if (!open || !originallyCheckedStudents || !classrooms || !toggledPath)
            return [];
        return originallyCheckedStudentsInPath(
            toggledPath,
            originallyCheckedStudents,
            classrooms
        )
            .filter(currentlyChecked(checkedStudents))
            .map((studentId) => getStudent(studentId, classrooms));
    }, [
        open,
        classrooms,
        checkedStudents,
        originallyCheckedStudents,
        toggledPath,
    ]);

    return (
        <Modal
            open={toggledStudents.length !== 0 && open}
            closable={false}
            onClose={onClose}
            title={intl.formatMessage(messages.deleteFromRecipients, {
                label:
                    toggledStudents.length === 1
                        ? fullName(toggledStudents[0])
                        : `${toggledStudents.length} ${intl.formatMessage(
                              commonMessages.students
                          )}`,
            })}
            okText={intl.formatMessage(messages.remove)}
            cancelText={intl.formatMessage(commonMessages.cancel)}
            onOk={onOk}
            onCancel={onCancel}
        >
            <div className="edit-assignment__confirmation-modal">
                <p>{intl.formatMessage(messages.deleteConfirmationMessage)}</p>
                {toggledStudents.length > 1 && (
                    <CondensedStudentsList students={toggledStudents} />
                )}
                <MessageDismiss
                    sessionMessageKey={
                        DismissableMessages.playlistRemoveStudentFromRecipients
                    }
                />
            </div>
        </Modal>
    );
};
export default ConfirmationModal;

const messages = defineMessages({
    deleteFromRecipients: {
        id: "playlist-editAssignment-removeFromRecipients",
        defaultMessage: "Delete {label} from recipients",
    },
    deleteConfirmationMessage: {
        id: "playlist-editAssignment-deleteConfirmationMessage",
        defaultMessage:
            "This student won't have access to the playlist anymore. Their progression can be restored if you add them back to the list later. In case you want to reset their progression, duplicate the playlist and add them as recipients of the duplicate playlist.",
    },
    remove: {
        id: "playlist-editAssignment-remove",
        defaultMessage: "Remove",
    },
});
